import React, { useEffect, useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { projectsAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';
import { useIsMobile } from '../../helpers/useMediaQuery';
import AdaptiveModal from '../../components/Common/AdaptiveModal';
import SiteDetailModal from '../Projects/SiteDetailModal';

interface Site {
  id: string;
  name: string;
  code: string;
  address: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  project: {
    id: string;
    name: string;
    code: string;
    city: { id: string; name: string; parentId?: string | null };
  };
  city?: { id: string; name: string; parentId?: string | null } | null;
}

interface Project {
  id: string;
  name: string;
  code: string;
  city: { id: string; name: string };
}

interface City {
  id: string;
  name: string;
  code?: string;
}

interface District {
  id: string;
  name: string;
}

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  COMPLETED: 'bg-info bg-opacity-10 text-info',
  SUSPENDED: 'bg-warning bg-opacity-10 text-warning',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  COMPLETED: 'Tamamlandı',
  SUSPENDED: 'Askıda',
};

const SiteListPage: React.FC = () => {
  const isAdmin = useIsAdmin();
  const isMobile = useIsMobile();
  const [sites, setSites] = useState<Site[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [cityFilter, setCityFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [filterCities, setFilterCities] = useState<City[]>([]);

  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  // Create modal
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cities, setCities] = useState<City[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
    status: 'ACTIVE',
    projectId: '',
    provinceId: '',
    districtId: '',
  });

  const openDetailModal = (site: Site) => {
    setSelectedSite(site);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedSite(null);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [sitesRes, projectsRes, citiesRes] = await Promise.all([
        projectsAPI.getSites(projectFilter || undefined),
        projectsAPI.getAll(),
        filterCities.length === 0 ? projectsAPI.getCities() : Promise.resolve(null),
      ]);
      let filteredSites = sitesRes.data;
      if (statusFilter) {
        filteredSites = filteredSites.filter((s: Site) => s.status === statusFilter);
      }
      if (cityFilter) {
        filteredSites = filteredSites.filter((s: Site) => {
          const siteCityId = s.city?.id;
          const siteCityParentId = s.city?.parentId;
          const projectCityId = s.project.city.id;
          return siteCityId === cityFilter || siteCityParentId === cityFilter || projectCityId === cityFilter;
        });
      }
      if (searchFilter) {
        const search = searchFilter.toLowerCase();
        filteredSites = filteredSites.filter((s: Site) =>
          s.name.toLowerCase().includes(search) ||
          s.code.toLowerCase().includes(search) ||
          s.project.name.toLowerCase().includes(search) ||
          s.project.code.toLowerCase().includes(search) ||
          (s.address && s.address.toLowerCase().includes(search))
        );
      }
      setSites(filteredSites);
      setProjects(projectsRes.data);
      if (citiesRes) setFilterCities(citiesRes.data);
      if (selectedSite) {
        const updated = filteredSites.find((s: Site) => s.id === selectedSite.id);
        if (updated) setSelectedSite(updated);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectFilter, statusFilter, cityFilter, searchFilter]);

  const toggleModal = () => {
    if (!modalOpen) {
      loadCities();
    } else {
      setFormData({ name: '', code: '', address: '', status: 'ACTIVE', projectId: '', provinceId: '', districtId: '' });
      setDistricts([]);
    }
    setModalOpen(!modalOpen);
  };

  const loadCities = async () => {
    try {
      const res = await projectsAPI.getCities();
      setCities(res.data);
    } catch (error) {
      console.error('İller yüklenemedi:', error);
    }
  };

  const handleProvinceChange = async (provinceId: string) => {
    setFormData((prev) => ({ ...prev, provinceId, districtId: '' }));
    setDistricts([]);
    if (provinceId) {
      try {
        setLoadingDistricts(true);
        const res = await projectsAPI.getDistricts(provinceId);
        setDistricts(res.data);
      } catch (error) {
        console.error('İlçeler yüklenemedi:', error);
      } finally {
        setLoadingDistricts(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.code || !formData.projectId) {
      toast.error('Zorunlu alanları doldurun');
      return;
    }

    try {
      setSubmitting(true);
      await projectsAPI.createSite({
        name: formData.name,
        code: formData.code,
        projectId: formData.projectId,
        address: formData.address || undefined,
        cityId: formData.districtId || formData.provinceId || undefined,
      });
      toast.success('Şantiye oluşturuldu');
      toggleModal();
      fetchData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0">Şantiyeler</h4>
            <div className="page-title-right d-flex align-items-center gap-2 flex-wrap">
              <Input
                type="text"
                bsSize="sm"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Ara..."
                style={{ width: '150px' }}
              />
              <Input
                type="select"
                bsSize="sm"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="">Tüm Projeler</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} - {p.name}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                bsSize="sm"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="">Tüm İller</option>
                {filterCities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </Input>
              <Input
                type="select"
                bsSize="sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ width: 'auto' }}
              >
                <option value="">Tüm Durumlar</option>
                <option value="ACTIVE">Aktif</option>
                <option value="COMPLETED">Tamamlandı</option>
                <option value="SUSPENDED">Askıda</option>
              </Input>
              {isAdmin && (
                <Button color="primary" onClick={toggleModal}>
                  <i className="ri-add-line me-1"></i>
                  Yeni Şantiye
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card>
            <CardHeader>
              <h5 className="card-title mb-0">Şantiye Listesi</h5>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                  </div>
                </div>
              ) : sites.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">Henüz şantiye bulunmuyor.</p>
                </div>
              ) : isMobile ? (
                // Mobile Card View
                <div className="admin-card-list">
                  {sites.map((site) => (
                    <div key={site.id} className="admin-card-item">
                      <div
                        className="admin-card-header"
                        onClick={() => openDetailModal(site)}
                        style={{ cursor: 'pointer' }}
                      >
                        <h6 className="admin-card-title">{site.name}</h6>
                        <span
                          className={`px-2 py-1 rounded-pill small ${statusStyles[site.status]}`}
                          style={{ fontWeight: 500 }}
                        >
                          {statusLabels[site.status]}
                        </span>
                      </div>

                      <div
                        className="admin-card-body"
                        onClick={() => openDetailModal(site)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-code-box-line"></i>Kod
                          </span>
                          <div className="admin-info-value value-highlight">
                            {site.code}
                          </div>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-building-4-line"></i>Proje Kodu
                          </span>
                          <div className="admin-info-value value-highlight">
                            {site.project.code}
                          </div>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-building-4-line"></i>Proje Adı
                          </span>
                          <span className="admin-info-value">
                            {site.project.name}
                          </span>
                        </div>
                        <div className="admin-info-row">
                          <span className="admin-info-label">
                            <i className="ri-map-pin-line"></i>İl / İlçe
                          </span>
                          <span className="admin-info-value">
                            {site.city?.parentId
                              ? `${site.project.city.name} / ${site.city.name}`
                              : site.city?.name || site.project.city.name}
                          </span>
                        </div>
                        {site.address && (
                          <div className="admin-info-row">
                            <span className="admin-info-label">
                              <i className="ri-map-2-line"></i>Adres
                            </span>
                            <span className="admin-info-value">
                              {site.address}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="admin-card-footer">
                        <button
                          type="button"
                          className="admin-action-btn admin-btn-primary"
                          style={{ flex: 1 }}
                          onClick={() => openDetailModal(site)}
                        >
                          <i className="ri-eye-line"></i>
                          Detaylar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Desktop Table View
                <div className="table-responsive">
                  <Table className="table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Şantiye Kodu</th>
                        <th>Şantiye Adı</th>
                        <th>Proje Kodu</th>
                        <th>Proje Adı</th>
                        <th>İl / İlçe</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sites.map((site) => (
                        <tr
                          key={site.id}
                          onClick={() => openDetailModal(site)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <strong>{site.code}</strong>
                          </td>
                          <td>{site.name}</td>
                          <td>
                            <strong>{site.project.code}</strong>
                          </td>
                          <td>{site.project.name}</td>
                          <td>
                            {site.city?.parentId
                              ? `${site.project.city.name} / ${site.city.name}`
                              : site.city?.name || site.project.city.name}
                          </td>
                          <td>
                            <span
                              className={`px-2 py-1 rounded-pill small ${statusStyles[site.status]}`}
                              style={{ fontWeight: 500 }}
                            >
                              {statusLabels[site.status]}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Yeni Şantiye Modal */}
      <AdaptiveModal
        isOpen={modalOpen}
        toggle={toggleModal}
        title="Yeni Şantiye"
        size="md"
        footer={
          <>
            <Button color="secondary" onClick={toggleModal}>
              İptal
            </Button>
            <Button color="primary" type="submit" form="site-page-form" disabled={submitting}>
              {submitting ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </>
        }
      >
        <Form id="site-page-form" onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Proje *</Label>
            <Input
              type="select"
              value={formData.projectId}
              onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
              required
            >
              <option value="">Proje Seçin</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} - {p.name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Şantiye Kodu *</Label>
                <Input
                  type="text"
                  value={formData.code}
                  onChange={(e) =>
                    setFormData({ ...formData, code: e.target.value.toUpperCase() })
                  }
                  placeholder="Örn: S-001"
                  required
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Durum</Label>
                <Input
                  type="select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="ACTIVE">Aktif</option>
                  <option value="SUSPENDED">Askıda</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Şantiye Adı *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Şantiye adını girin"
              required
            />
          </FormGroup>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>İl</Label>
                <Input
                  type="select"
                  value={formData.provinceId}
                  onChange={(e) => handleProvinceChange(e.target.value)}
                >
                  <option value="">İl Seçin</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.code ? `${city.code} - ${city.name}` : city.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>İlçe</Label>
                <Input
                  type="select"
                  value={formData.districtId}
                  onChange={(e) => setFormData({ ...formData, districtId: e.target.value })}
                  disabled={!formData.provinceId || loadingDistricts}
                >
                  <option value="">
                    {loadingDistricts ? 'Yükleniyor...' : 'İlçe Seçin (Opsiyonel)'}
                  </option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Adres</Label>
            <Input
              type="textarea"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Şantiye adresi (opsiyonel)"
              rows={2}
            />
          </FormGroup>
        </Form>
      </AdaptiveModal>

      {/* Şantiye Detay Modal */}
      <SiteDetailModal
        isOpen={detailModalOpen}
        toggle={closeDetailModal}
        site={selectedSite}
        onUpdate={fetchData}
      />
    </>
  );
};

export default SiteListPage;
